import React, { useEffect, useState } from 'react';
import { ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEOHead from '../components/SEOHead';
import { api } from '../services/api';

// Custom renderers com Tailwind (sem depender de @tailwindcss/typography).
function H2({ children, ...p }) { return <h2 className="text-xl font-bold mt-8 mb-4" {...p}>{children}</h2>; }
function H3({ children, ...p }) { return <h3 className="text-lg font-semibold mt-6 mb-3" {...p}>{children}</h3>; }
function Para({ children, ...p }) { return <p className="text-gray-600 leading-relaxed mb-4" {...p}>{children}</p>; }
function UL({ children, ...p }) { return <ul className="text-gray-600 space-y-2 list-disc pl-6 mb-4" {...p}>{children}</ul>; }
function LI({ children, ...p }) { return <li className="leading-relaxed" {...p}>{children}</li>; }
function Strong({ children, ...p }) { return <strong className="font-semibold text-gray-800" {...p}>{children}</strong>; }
function EmT({ children, ...p }) { return <em className="italic" {...p}>{children}</em>; }
function TableWrap({ children, ...p }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
      <table className="min-w-full text-sm" {...p}>{children}</table>
    </div>
  );
}
function THead({ children, ...p }) { return <thead className="bg-gray-50 text-gray-700" {...p}>{children}</thead>; }
function TBody({ children, ...p }) { return <tbody className="divide-y divide-gray-200 text-gray-600" {...p}>{children}</tbody>; }
function TH({ children, ...p }) { return <th className="px-4 py-2 text-left font-semibold" {...p}>{children}</th>; }
function TD({ children, ...p }) { return <td className="px-4 py-2 align-top" {...p}>{children}</td>; }

function PoliticaLink({ navigate, href, children, ...rest }) {
  const isInternal = href && href.startsWith('/');
  if (isInternal) {
    return (
      <button
        type="button"
        onClick={() => navigate(href)}
        className="text-[var(--primary)] hover:underline font-medium"
        {...rest}
      >
        {children}
      </button>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--primary)] hover:underline"
      {...rest}
    >
      {children}
    </a>
  );
}

export default function PoliticaDePrivacidade() {
  const navigate = useNavigate();
  const lastUpdated = '26 de Janeiro de 2026';

  // Puxa o mesmo conteúdo que o admin edita em /admin/conteudo/politica.
  // Backend faz fallback: (1) versão custom do município → (2) versão global
  // → (3) markdown default do core/default_pages_content.py.
  const [conteudo, setConteudo] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/v2/admin/paginas/public/lgpd')
      .then((r) => { if (alive) setConteudo(r.data?.conteudo || ''); })
      .catch(() => { if (alive) setConteudo(''); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const mdComponents = React.useMemo(() => ({
    h1: H2,
    h2: H2,
    h3: H3,
    p: Para,
    ul: UL,
    ol: UL,
    li: LI,
    strong: Strong,
    em: EmT,
    table: TableWrap,
    thead: THead,
    tbody: TBody,
    th: TH,
    td: TD,
    // eslint-disable-next-line react/no-unstable-nested-components
    a: (props) => <PoliticaLink navigate={navigate} {...props} />,
  }), [navigate]);

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title="Política de Privacidade"
        description="Saiba como o Empreende Comunidade coleta, armazena e protege seus dados pessoais conforme a LGPD."
        canonical="/politica-de-privacidade"
      />
      {/* Header */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--primary)] hover:underline mb-6"
            data-testid="privacy-voltar-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[var(--primary)]" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
              Política de Privacidade
            </h1>
          </div>
          <p className="text-gray-500 text-sm">
            Última atualização: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* LGPD Badge */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Shield className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="font-semibold text-green-800">Conformidade com a LGPD</p>
            <p className="text-sm text-green-700">
              Esta política está em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018)
            </p>
          </div>
        </div>

        <div
          className="bg-white rounded-2xl shadow-sm p-8 max-w-none break-words [overflow-wrap:anywhere]"
          data-testid="privacy-conteudo"
        >
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando política…
            </div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {conteudo}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
}
